import { useContext, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, TextField, IconButton, Typography } from '@mui/material';
import { Add, Delete, Save, Close } from '@mui/icons-material';
import { Loader, Modal } from '../../../components';
import {
  createUnassignedFieldWalkthroughItems,
  getFieldWalkthroughDetail,
  getFieldWalkthroughLocations
} from '../../../fetch';
import {
  IFieldWalkthroughDetailItem,
  IUnassignedFieldWalkthroughItem,
  FieldWalkthroughResponse
} from '../../../models';
import { UserContext } from '../../../context';

interface IAddWalkthroughItemsModal {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  fieldWalkthroughId: string;
  setSelectedDeficientItems?: (items: IFieldWalkthroughDetailItem[]) => void;
}

export const AddWalkthroughItemsModal = ({
  open,
  onClose,
  fieldWalkthroughId,
  setSelectedDeficientItems
}: IAddWalkthroughItemsModal) => {
  const { isPM, isSupervisor, isForeman } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [items, setItems] = useState<IUnassignedFieldWalkthroughItem[]>([{ text: '' }]);
  const [initialItems] = useState<IUnassignedFieldWalkthroughItem[]>([{ text: '' }]);

  const handleAddItem = () => {
    setItems([...items, { text: '' }]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { text: value };
    setItems(newItems);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const nonEmptyItems = items.filter(item => item.text.trim() !== '');
      await createUnassignedFieldWalkthroughItems(fieldWalkthroughId, nonEmptyItems);
      enqueueSnackbar("Items added successfully to the 'Unassigned' location", {
        variant: 'success'
      });
      setItems([{ text: '' }]);
      // refetch locations to get the updated list since maybe this the first time a new ad hoc item is added
      const locations = await getFieldWalkthroughLocations(fieldWalkthroughId);
      // find the location ID for 'Unassigned'
      const unassignedLocationId = locations.find(
        location => location.fieldWalkthroughLocationName === 'Unassigned'
      )?.fieldWalkthroughLocationID;
      // call the fieldwalkthrough detail API to get the items for the 'Unassigned' location
      const unassignedFieldWalkthroughLocation = await getFieldWalkthroughDetail(
        fieldWalkthroughId,
        +unassignedLocationId
      );
      // filter the items to only include those that are not already assigned to a punchlist
      const filteredPunchlistItems = unassignedFieldWalkthroughLocation?.items?.filter(
        item => !item.hasPunchlistItem
      );

      if (filteredPunchlistItems?.length > 0) {
        const items = filteredPunchlistItems?.map(item => ({
          ...item,
          foremanResponse: isForeman
            ? FieldWalkthroughResponse.RepairReplace
            : item.foremanResponse,
          supervisorResponse: isSupervisor
            ? FieldWalkthroughResponse.RepairReplace
            : item.supervisorResponse,
          projectManagerResponse: isPM
            ? FieldWalkthroughResponse.RepairReplace
            : item.projectManagerResponse
        }));
        setSelectedDeficientItems(items);
      }
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error adding items', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    const hasChanges = items.some((item, index) => item.text !== initialItems[index]?.text);
    if (hasChanges) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        setItems([{ text: '' }]);
        onClose();
      }
    } else {
      setItems([{ text: '' }]);
      onClose();
    }
  };

  const isSaveDisabled = items.some(item => !item.text.trim());

  return (
    <Modal open={open} onClose={handleClose} maxWidth="sm">
      {isSaving && <Loader type="overlay" position="centered" />}
      <Box>
        <Typography variant="h5" gutterBottom>
          Add Manual FWT Items
        </Typography>

        {items.map((item, index) => (
          <Box key={index} display="flex" alignItems="center" mb={1}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={item.text}
              onChange={e => handleItemChange(index, e.target.value)}
              placeholder="Item text"
            />
            <IconButton
              onClick={() => handleRemoveItem(index)}
              disabled={items.length === 1}
              size="small"
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        ))}

        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Button color="secondary" startIcon={<Add />} onClick={handleAddItem} variant="contained">
            Add
          </Button>
        </Box>

        <Box>
          <Button
            startIcon={<Save />}
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaveDisabled}
          >
            Save
          </Button>
          <Button
            startIcon={<Close />}
            type="button"
            variant="contained"
            color="inherit"
            onClick={handleClose}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
